import { motion } from 'framer-motion';
import {
  GripVertical,
  Settings,
  Type,
  Play,
  Code,
  HelpCircle,
  FileText,
  Trash2,
  Image,
  Save,
  Info,
} from 'lucide-react';
import { Button, Card, CardBody, addToast } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  FormTextarea,
  FormSelect,
  FormNumberInput,
  FormCategorySelector,
  FormProductSelector,
  FormCheckbox,
  ConfirmationModal,
} from '../base';
import { STEP_TYPES, DIFFICULTIES, LANGUAGES } from './constants';
import { useGuideBuilderGuide } from '../../contexts/GuideBuilderContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { zodResolver } from '@hookform/resolvers/zod';
import { GuideDifficulty, GuideLanguage, GuideStepType } from '@/services/apis/guides';
import { GuideDetailsFormData, TransformedStep, guideDetailsFormSchema } from './schemas';
import { useAddStep, useUpdateGuideDetails, useDeleteStep, useUpdateStep } from '@/hooks/useGuides';
import { useEffect, useState, useMemo } from 'react';
import CategoriesModal from '../admin/CategoriesModal';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Map icon names to actual components
const iconMap = {
  Type,
  Play,
  Code,
  HelpCircle,
  FileText,
  Image,
} as const;

const stepTypes = STEP_TYPES.map((stepType) => ({
  ...stepType,
  icon: iconMap[stepType.icon as keyof typeof iconMap],
}));

const difficulties = DIFFICULTIES;

const GuideDetailsFormCard = () => {
  const {
    guide: { description, difficulty, estimatedTime, language, categoryId, productId, isRecommended, id: guideId },
    state: { isTranslating },
  } = useGuideBuilderGuide();
  const { currentLanguage } = useLanguage();
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid, isSubmitting },
    reset,
  } = useForm<GuideDetailsFormData>({
    resolver: zodResolver(guideDetailsFormSchema),
    mode: 'onChange',
    defaultValues: {
      description: description || '',
      difficulty: difficulty || GuideDifficulty.BEGINNER,
      estimatedTime: estimatedTime ?? undefined,
      language: language || currentLanguage,
      productId: productId || '',
      categoryId: categoryId || '',
      isRecommended: isRecommended || false,
    },
  });
  // reset form when guide changes
  useEffect(() => {
    reset({
      description: description || '',
      difficulty: difficulty || GuideDifficulty.BEGINNER,
      estimatedTime: estimatedTime ?? undefined,
      language: language || currentLanguage,
      productId: productId || '',
      categoryId: categoryId || '',
      isRecommended: isRecommended || false,
    });
  }, [description, difficulty, estimatedTime, language, productId, categoryId, isRecommended, currentLanguage, reset]);
  const { mutateAsync: updateGuideDetails } = useUpdateGuideDetails(guideId!);
  const handleFormSubmit: SubmitHandler<GuideDetailsFormData> = async (data) => {
    if (isTranslating) return;
    const values = {
      description: data.description,
      difficulty: data.difficulty,
      estimatedTime: typeof data.estimatedTime === 'number' ? data.estimatedTime : undefined,
      productId: data.productId,
      categoryId: data.categoryId,
      isRecommended: data.isRecommended || false,
    };
    await updateGuideDetails({ lang: currentLanguage as GuideLanguage, data: values });
    reset(values);
  };
  const { t } = useTranslation('admin');
  return (
    <Card className="m-6 mb-0">
      <CardBody>
        <h3 className="font-semibold text-gray-900 mb-4">{t('guides.builder.sidebar.tutorialDetails')}</h3>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <div className="space-y-4">
            <FormTextarea
              name="description"
              control={control}
              label={t('guides.builder.sidebar.description')}
              placeholder={t('guides.builder.sidebar.descriptionPlaceholder')}
              rows={3}
            />

            <div className="grid grid-cols-2 gap-3">
              <FormSelect
                name="difficulty"
                control={control}
                label={t('guides.builder.sidebar.difficulty')}
                options={difficulties.map((diff) => ({
                  key: diff.value,
                  value: diff.value,
                  label: t(`guides.builder.difficulty.${diff.value}`),
                }))}
              />
              <FormNumberInput
                name="estimatedTime"
                control={control}
                label={t('guides.builder.sidebar.time')}
                placeholder={t('guides.builder.sidebar.timePlaceholder')}
              />
            </div>

            <FormSelect
              name="language"
              control={control}
              label={t('guides.builder.sidebar.language')}
              disabled
              isDisabled
              options={LANGUAGES.map((language) => ({
                key: language.value,
                value: language.value,
                label: t(`guides.builder.languages.${language.value}`),
              }))}
            />

            <div className="flex items-center space-x-2">
              <FormCategorySelector name="categoryId" control={control} label={t('guides.builder.sidebar.category')} />
              <Button
                isIconOnly
                variant="light"
                onPress={() => setIsCategoriesModalOpen(true)}
                title={t('guides.builder.sidebar.category')}
              >
                <Settings className="w-4 h-4" />
              </Button>
            </div>
            <span className="text-xs text-gray-500">{t('guides.builder.sidebar.productDescription')}</span>
            <FormProductSelector name="productId" control={control} label={t('guides.builder.sidebar.product')} />
            <div className="flex items-center gap-2">
              <FormCheckbox name="isRecommended" control={control} label={t('guides.builder.sidebar.isRecommended')} />
              <div className="relative group">
                <Info className="w-4 h-4 text-gray-400 cursor-help" />
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 w-64">
                  <div className="bg-gray-900 text-white text-xs rounded-lg shadow-lg p-3 border border-gray-700">
                    <div className="flex items-start space-x-2">
                      <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-gray-200 leading-relaxed">
                        {t('guides.builder.sidebar.isRecommendedTooltip')}
                      </p>
                    </div>
                    <div className="absolute top-full left-1/2 -translate-x-1/2">
                      <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {isDirty && (
            <div className="flex justify-end">
              <Button
                type="submit"
                isLoading={isSubmitting}
                disabled={!isValid || isSubmitting || isTranslating}
                variant="solid"
                color="primary"
                startContent={<Save className="w-4 h-4" />}
              >
                {t('common.save')}
              </Button>
            </div>
          )}
        </form>
        <CategoriesModal isOpen={isCategoriesModalOpen} onClose={() => setIsCategoriesModalOpen(false)} />
      </CardBody>
    </Card>
  );
};

const hasStepContent = (step: TransformedStep): boolean => {
  if (!step.content) {
    return false;
  }

  const content = step.content;
  switch (step.type) {
    case GuideStepType.RICH_TEXT:
      return !!(content.text && content.text.trim().length > 0);
    case GuideStepType.VIDEO:
      return !!(content.videos && Array.isArray(content.videos) && content.videos.length > 0);
    case GuideStepType.IMAGE:
      return !!(content.images && Array.isArray(content.images) && content.images.length > 0);
    case GuideStepType.PDF:
      return !!(content.pdf && content.pdf.trim().length > 0);
    case GuideStepType.QUESTION:
      return !!(content.questions && Array.isArray(content.questions) && content.questions.length > 0);
    default:
      return false;
  }
};

interface SortableStepItemProps {
  step: TransformedStep;
  index: number;
  stepType: (typeof stepTypes)[number] | undefined;
  isSelected: boolean;
  isOver: boolean;
  isDragging: boolean;
  onSelect: () => void;
  onDelete: () => void;
  t: (key: string) => string;
}

const SortableStepItem = ({
  step,
  index,
  stepType,
  isSelected,
  isOver,
  isDragging: _isDragging,
  onSelect,
  onDelete,
  t,
}: SortableStepItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleClick = () => {
    if (!isDragging) {
      onSelect();
    }
  };

  return (
    <>
      {isOver && !isDragging && (
        <motion.div
          className="h-0.5 bg-primary-500 rounded-full mx-2 mb-2 shadow-sm"
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          exit={{ scaleX: 0, opacity: 0 }}
          transition={{ duration: 0.15 }}
        />
      )}
      <motion.div
        ref={setNodeRef}
        style={style}
        className={`p-3 rounded-lg border cursor-pointer transition-all relative ${
          isSelected ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
        } ${isDragging ? 'shadow-lg z-50 opacity-50' : ''} ${
          isOver && !isDragging
            ? 'border-primary-400 border-2 bg-primary-50/70 ring-2 ring-primary-200 ring-opacity-50 shadow-md'
            : ''
        }`}
        onClick={handleClick}
        initial={{ opacity: 0, y: 10 }}
        animate={{
          opacity: isDragging ? 0.5 : isOver && !isDragging ? 1 : 1,
          y: 0,
          scale: isOver && !isDragging ? 1.02 : 1,
        }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      >
        <div className="flex items-center space-x-3">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing touch-none"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="w-4 h-4 text-gray-400 hover:text-gray-600" />
          </div>
          <div
            className={`w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-medium bg-gradient-to-r ${
              stepType?.color || 'from-gray-500 to-gray-600'
            }`}
          >
            {stepType?.icon && <stepType.icon className="w-4 h-4" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate" title={step.title}>
              {step.title}
            </p>
            <p className="text-xs text-panel-foreground capitalize">
              {t(`guides.builder.stepTypes.${step.type}`)} • {t('guides.builder.sidebar.steps')} {index + 1}
            </p>
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            <Button isIconOnly size="sm" variant="light" color="danger" onPress={onDelete} title={t('common.delete')}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </motion.div>
    </>
  );
};

const StepsListCard = () => {
  const { t } = useTranslation('admin');
  const {
    guide: { steps, id: guideId },
    actions: { onSelectStep },
    state: { selectedStep },
  } = useGuideBuilderGuide();
  const [stepToDelete, setStepToDelete] = useState<{
    id: string;
    translations?: Array<{ content?: any }>;
    type: GuideStepType;
  } | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const { mutateAsync: addStep } = useAddStep(guideId!);
  const { mutateAsync: deleteStep, isPending: isDeleting } = useDeleteStep(guideId!);
  const { mutateAsync: updateStep } = useUpdateStep(guideId!);

  // Sort steps by position to ensure correct order
  const sortedSteps = useMemo(() => {
    return [...steps].sort((a, b) => a.position - b.position);
  }, [steps]);

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const handleAddStep = async (type: GuideStepType) => {
    try {
      const position = (sortedSteps[sortedSteps.length - 1]?.position || 0) + 1.0;
      const newStep = { position, type, title: 'New step' };
      const addedStep = await addStep(newStep);
      onSelectStep({ id: addedStep.data?.id || '', title: 'New step', type, position });
    } catch (error: any) {
      addToast({
        title: t('guides.builder.stepAddError'),
        description: error.message || t('guides.builder.stepAddError'),
        color: 'danger',
      });
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    setActiveId(active.id as string);
    setOverId(over?.id as string | null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);
    setOverId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = sortedSteps.findIndex((step) => step.id === active.id);
    const newIndex = sortedSteps.findIndex((step) => step.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const reorderedSteps = arrayMove(sortedSteps, oldIndex, newIndex);

    // Calculate new positions using fractional ordering
    const newPositions: { stepId: string; position: number }[] = [];
    reorderedSteps.forEach((step, index) => {
      const newPosition = index + 1.0;
      if (step.position !== newPosition) {
        newPositions.push({ stepId: step.id, position: newPosition });
      }
    });

    // Update all affected steps in parallel
    if (newPositions.length > 0) {
      try {
        await Promise.all(newPositions.map(({ stepId, position }) => updateStep({ stepId, data: { position } })));
      } catch (error: any) {
        addToast({
          title: t('guides.builder.stepReorderError'),
          description: error.message || t('guides.builder.stepReorderError'),
          color: 'danger',
        });
      }
    }
  };
  const handleDeleteStep = async (step: TransformedStep) => {
    if (hasStepContent(step)) {
      setStepToDelete(step);
      setIsDeleteModalOpen(true);
    } else {
      try {
        await deleteStep(step.id);
        if (selectedStep && selectedStep.id === step.id) {
          onSelectStep(undefined);
        }
      } catch (error) {
        console.error('Error deleting step:', error);
      }
    }
  };
  const confirmDeleteStep = async () => {
    if (!stepToDelete) return;
    try {
      await deleteStep(stepToDelete.id);
      if (selectedStep && selectedStep.id === stepToDelete.id) {
        onSelectStep(undefined);
      }
      setIsDeleteModalOpen(false);
      setStepToDelete(null);
    } catch (error) {
      console.error('Error deleting step:', error);
    }
  };
  const cancelDeleteStep = () => {
    setIsDeleteModalOpen(false);
    setStepToDelete(null);
  };
  return (
    <Card className="m-6 flex-1">
      <CardBody>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">
            {t('guides.builder.sidebar.steps')} ({sortedSteps.length})
          </h3>
          <div className="flex space-x-1">
            {stepTypes.map((stepType) => (
              <Button
                key={stepType.type}
                size="sm"
                isIconOnly
                onPress={() => handleAddStep(stepType.type)}
                className={`text-white bg-gradient-to-r ${stepType.color}`}
                title={t('guides.builder.sidebar.addStep', { type: t(`guides.builder.stepTypes.${stepType.type}`) })}
              >
                <stepType.icon className="w-4 h-4" />
              </Button>
            ))}
          </div>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={(event) => setActiveId(event.active.id as string)}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={() => {
            setActiveId(null);
            setOverId(null);
          }}
        >
          <SortableContext items={sortedSteps.map((step) => step.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {sortedSteps.map((step, index) => {
                const stepType = stepTypes.find((st) => st.type === step.type);
                const isOver = overId === step.id && activeId !== step.id;
                const isDragging = activeId === step.id;
                return (
                  <SortableStepItem
                    key={step.id}
                    step={step}
                    index={index}
                    stepType={stepType}
                    isSelected={selectedStep?.id === step.id}
                    isOver={isOver}
                    isDragging={isDragging}
                    onSelect={() => onSelectStep(step)}
                    onDelete={() => handleDeleteStep(step)}
                    t={t}
                  />
                );
              })}

              {sortedSteps.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-sm">{t('guides.builder.sidebar.noSteps')}</p>
                  <p className="text-xs">{t('guides.builder.sidebar.noStepsDescription')}</p>
                </div>
              )}
            </div>
          </SortableContext>
        </DndContext>
      </CardBody>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={cancelDeleteStep}
        onConfirm={confirmDeleteStep}
        title={t('guides.builder.deleteStep.confirmTitle')}
        message={t('guides.builder.deleteStep.confirmMessage')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        confirmColor="danger"
        isLoading={isDeleting}
      />
    </Card>
  );
};

const GuideSidebar: React.FC = () => {
  return (
    <div>
      {/* Tutorial Metadata */}
      <GuideDetailsFormCard />
      {/* Steps List */}
      <StepsListCard />
    </div>
  );
};

export default GuideSidebar;
